import React from 'react'
import AntdCheckbox from 'antd/es/checkbox'
import type { CheckboxRef } from 'rc-checkbox'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type {
  CheckboxGroupProps,
  CheckboxProps,
  CheckboxValueType,
} from './antd-props'
import { Checkbox as ShadcnSeamCheckbox } from './Checkbox.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/checkbox` — one of the three plain form
 * controls put behind the design toggle in the same PR (Checkbox, Radio,
 * Switch), after `./Button` (#970), the presentational batch, the two portal
 * seams (#982), `./Spin` (#983) and the login page (#984).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd's whole component barrel into whatever
 * chunk imports it.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch each dispatcher below renders
 * antd's own component with the props it was given AND the ref it was given:
 * same element, same class names, same DOM, same imperative handle.
 * `./__tests__/checkbox-seam-parity.test.tsx` is the test to keep green.
 *
 * `Group` is carried across explicitly rather than by copying antd's component
 * object: `Object.assign(dispatcher, AntdCheckbox)` would also copy a
 * forwardRef object's `$$typeof` and `render` and turn this into something
 * React cannot render. It dispatches exactly as the root does, so the seam
 * cannot end up half-switched — which matters more here than for most seams,
 * because a Group and its children talk through a React context that only one
 * of the two implementations provides.
 *
 * NOT REACHABLE FROM HERE, and named rather than left to be rediscovered:
 * antd renders its OWN Checkbox inside `Table`'s `rowSelection` and inside
 * `Transfer`, from within the package. Those stay antd in both modes, exactly
 * as antd's own `Spin` did before `./Spin/AntdDefaultIndicator` — and unlike
 * Spin there is no app-wide lever to redirect them. A switched list page is
 * therefore MIXED by construction: seam checkboxes in the filter panel,
 * antd's in the selection column.
 */
const CheckboxDispatcher = React.forwardRef<CheckboxRef, CheckboxProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamCheckbox {...props} ref={ref} />

    return <AntdCheckbox {...props} ref={ref} />
  }
)

CheckboxDispatcher.displayName = 'Checkbox'

const GroupDispatcherBase = React.forwardRef<
  HTMLDivElement,
  CheckboxGroupProps
>((props, ref) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn')
    return <ShadcnSeamCheckbox.Group {...props} ref={ref} />

  return <AntdCheckbox.Group {...props} ref={ref} />
})

GroupDispatcherBase.displayName = 'Checkbox.Group'

// antd declares its Group as a generic function rather than a
// `ForwardRefExoticComponent`; keeping that shape is what makes
// `value`/`onChange` infer together at a call site exactly as they do today.
const GroupDispatcher = GroupDispatcherBase as <
  T extends CheckboxValueType = CheckboxValueType
>(
  props: CheckboxGroupProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement

export const Checkbox = Object.assign(CheckboxDispatcher, {
  Group: GroupDispatcher,
})
